// ════════════════════════════════════════════════════════════════
// Разбор недели: связки между состоянием и едой.
//
// Принцип: связки ищет КОД по заранее заданным гипотезам, а не модель.
// На семи днях статистики не существует — нейросеть на таком объёме
// уверенно придумает закономерности, которых нет. Поэтому здесь только
// арифметика и пороги, а формулировки — заготовки.
//
// Всё, что выходит наружу, подаётся как ГИПОТЕЗА («похоже», «заметил»),
// никогда как доказанный факт: семь дней — это наблюдение, не наука.
// ════════════════════════════════════════════════════════════════

export interface DayRow {
  date: string;
  mood: number | null;
  sleep: number | null;
  tags: string[];
  note: string | null;
  calories: number;
  carbs: number;
  eveningCalories: number; // съедено после 20:00
  mealCount: number;
}

export interface Connection {
  id: string;
  text: string;
  /** сколько дней в каждой группе — показываем, чтобы не выглядело как истина */
  basis: string;
}

export interface WeekReport {
  filledDays: number;
  /** сколько дней вообще есть отметка дня */
  level: 'none' | 'teaser' | 'partial' | 'full';
  /** тревожный флаг: разбирать еду сейчас неуместно */
  concern: boolean;
  facts: string[];
  connections: Connection[];
  slavaLine: string;
}

const MIN_GROUP = 2; // меньше двух дней в группе — не сравниваем вообще
const MIN_DIFF = 0.2; // расхождение меньше 20% — шум, молчим

const avg = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0);

/** Сравнивает две группы дней. Возвращает относительную разницу или null. */
function compare(a: number[], b: number[]): number | null {
  if (a.length < MIN_GROUP || b.length < MIN_GROUP) return null;
  const [ma, mb] = [avg(a), avg(b)];
  if (mb <= 0) return null;
  const diff = (ma - mb) / mb;
  return Math.abs(diff) >= MIN_DIFF ? diff : null;
}

/** 1 день, 2 дня, 5 дней — иначе текст выдаёт машину */
function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/**
 * «на 60% больше» звучит нормально, «на 261% больше» — уже нет.
 * Начиная с полутора крат переходим на разы: так человек читает без счёта в уме.
 */
function moreBy(diff: number): string {
  const ratio = 1 + Math.abs(diff);
  if (ratio < 1.5) return `на ${Math.round(Math.abs(diff) * 100)}% больше`;
  const rounded = Math.round(ratio * 10) / 10;
  const isWhole = Math.abs(rounded - Math.round(rounded)) < 0.05;
  const n = isWhole ? Math.round(rounded) : rounded;
  const word = isWhole ? plural(n as number, 'раз', 'раза', 'раз') : 'раза';
  return `почти в ${String(n).replace('.', ',')} ${word} больше`;
}

// ── Гипотезы ────────────────────────────────────────────────────
// Каждая проверяется отдельно и молчит, если данных мало или разница мелкая.

function badSleepMoreCarbs(days: DayRow[]): Connection | null {
  const bad = days.filter((d) => d.sleep !== null && d.sleep <= 2 && d.carbs > 0);
  const good = days.filter((d) => d.sleep !== null && d.sleep >= 4 && d.carbs > 0);
  const diff = compare(bad.map((d) => d.carbs), good.map((d) => d.carbs));
  if (diff === null || diff <= 0) return null;
  return {
    id: 'sleep-carbs',
    text: `После плохих ночей ты съедаешь ${moreBy(diff)} углеводов, чем после нормальных. Это не про характер — недосып правда меняет тягу к быстрой энергии.`,
    basis: `${bad.length} ${plural(bad.length, 'ночь', 'ночи', 'ночей')} против ${good.length}`,
  };
}

function hardDayEveningLoad(days: DayRow[]): Connection | null {
  const hard = days.filter((d) => d.mood !== null && d.mood <= 2);
  const ok = days.filter((d) => d.mood !== null && d.mood >= 4);
  const diff = compare(hard.map((d) => d.eveningCalories), ok.map((d) => d.eveningCalories));
  if (diff === null || diff <= 0) return null;
  return {
    id: 'mood-evening',
    text: `В тяжёлые дни вечер выходит плотнее — после восьми ты съедаешь ${moreBy(diff)}, чем в спокойные. Похоже, еда вечером берёт на себя работу, которую не сделал день.`,
    basis: `${hard.length} ${plural(hard.length, 'тяжёлый день', 'тяжёлых дня', 'тяжёлых дней')} против ${ok.length} спокойных`,
  };
}

function goodDayCloserToTarget(days: DayRow[], target: number): Connection | null {
  if (target <= 0) return null;
  const off = (d: DayRow) => Math.abs(d.calories - target) / target;
  const good = days.filter((d) => d.mood !== null && d.mood >= 4 && d.calories > 0);
  const hard = days.filter((d) => d.mood !== null && d.mood <= 2 && d.calories > 0);
  const diff = compare(good.map(off), hard.map(off));
  if (diff === null || diff >= 0) return null;
  return {
    id: 'mood-target',
    text: 'В дни, когда тебе хорошо, ты почти без усилий попадаешь в свою норму. В тяжёлые — разлёт заметно больше. Это к тому, что «дисциплина» во многом зависит от состояния, а не наоборот.',
    basis: `${good.length} хороших дня против ${hard.length} тяжёлых`,
  };
}

function restrictionRebound(days: DayRow[], target: number): Connection | null {
  if (target <= 0) return null;
  let hits = 0;
  for (let i = 0; i < days.length - 1; i++) {
    const today = days[i];
    const next = days[i + 1];
    if (today.calories > 0 && next.calories > 0 && today.calories < target * 0.7 && next.calories > target * 1.1) {
      hits++;
    }
  }
  if (hits < 1) return null;
  return {
    id: 'restriction-rebound',
    text:
      hits === 1
        ? 'Один раз за неделю сработала качель: сильно недобрал(а) за день — и на следующий вышел перебор. Организм добирает своё, просто с задержкой.'
        : `Качель сработала ${hits} ${plural(hits, 'раз', 'раза', 'раз')}: за днём сильного недобора шёл день перебора. Это не срыв воли — так тело возвращает недоданное.`,
    basis: hits === 1 ? 'один случай за неделю' : `${hits} ${plural(hits, 'случай', 'случая', 'случаев')} за неделю`,
  };
}

function tagEveningLoad(days: DayRow[]): Connection | null {
  const STRESS = ['работа', 'дедлайн', 'ссора', 'одиноко'];
  const stressed = days.filter((d) => d.tags?.some((t) => STRESS.includes(t)));
  const calm = days.filter((d) => !d.tags?.some((t) => STRESS.includes(t)));
  const diff = compare(stressed.map((d) => d.eveningCalories), calm.map((d) => d.eveningCalories));
  if (diff === null || diff <= 0) return null;
  const top = stressed.flatMap((d) => d.tags).filter((t) => STRESS.includes(t))[0];
  return {
    id: 'tag-evening',
    text: `В дни, которые ты отметил(а) словом «${top}», вечер выходит ${moreBy(diff)}. Стоит присмотреться — похоже, это твой личный спусковой крючок.`,
    basis: `${stressed.length} ${plural(stressed.length, 'такой день', 'таких дня', 'таких дней')} против ${calm.length}`,
  };
}

// ── Сборка отчёта ───────────────────────────────────────────────

export function buildWeekReport(days: DayRow[], targetCalories: number): WeekReport {
  const marked = days.filter((d) => d.mood !== null);
  const filledDays = marked.length;

  const level: WeekReport['level'] =
    filledDays >= 7 ? 'full' : filledDays >= 5 ? 'partial' : filledDays >= 3 ? 'teaser' : 'none';

  // Три тяжёлых дня подряд — разговор уже не про еду.
  // Разбирать пищевое поведение у человека, которому по-настоящему плохо, нельзя.
  let streak = 0;
  let concern = false;
  for (const d of days) {
    if (d.mood !== null && d.mood <= 2) {
      streak++;
      if (streak >= 3) concern = true;
    } else if (d.mood !== null) {
      streak = 0;
    }
  }

  if (level === 'none') {
    return {
      filledDays,
      level,
      concern: false,
      facts: [],
      connections: [],
      slavaLine:
        filledDays === 0
          ? 'Пока мне нечего сопоставлять. Отмечай день — это десять секунд вечером, — и в воскресенье покажу, что увидел.'
          : `Отметок пока ${filledDays}. По ним я вижу кусок, а не картину. Дай мне ещё пару дней — и разбор будет содержательным.`,
    };
  }

  if (concern) {
    return {
      filledDays,
      level,
      concern: true,
      facts: [],
      connections: [],
      slavaLine:
        'Три дня подряд ты отмечаешь, что тяжело. Разбирать сейчас твою еду я не буду — по-моему, дело не в ней.\n\nЕсли это накопилось и не отпускает, поговори с живым человеком: близким, врачом, психологом. Это не слабость, это нормальный способ разгрузиться. А еда подождёт, честное слово.',
    };
  }

  // Факты — есть всегда, даже когда связок не набралось
  const facts: string[] = [];
  const moods = marked.map((d) => d.mood as number);
  const sleeps = days.filter((d) => d.sleep !== null).map((d) => d.sleep as number);

  facts.push(`Средняя оценка дня — ${avg(moods).toFixed(1)} из 5`);
  if (sleeps.length >= 2) facts.push(`Сон — ${avg(sleeps).toFixed(1)} из 5`);

  const hardest = marked.reduce((a, b) => ((a.mood as number) <= (b.mood as number) ? a : b));
  if ((hardest.mood as number) <= 2) {
    const why = hardest.tags?.length ? `, и рядом стоит «${hardest.tags[0]}»` : '';
    facts.push(`Самый тяжёлый день оценён на ${hardest.mood}${why}`);
  }

  if (targetCalories > 0) {
    const inTarget = days.filter(
      (d) => d.calories > 0 && Math.abs(d.calories - targetCalories) / targetCalories <= 0.1,
    ).length;
    if (inTarget > 0) {
      facts.push(`Попал(а) в норму по калориям ${inTarget} ${plural(inTarget, 'день', 'дня', 'дней')}`);
    }
  }

  // Связки — по убыванию ценности для аудитории
  const all = [
    badSleepMoreCarbs(days),
    hardDayEveningLoad(days),
    restrictionRebound(days, targetCalories),
    tagEveningLoad(days),
    goodDayCloserToTarget(days, targetCalories),
  ].filter(Boolean) as Connection[];

  const maxConnections = level === 'full' ? 4 : level === 'partial' ? 2 : 1;
  const connections = all.slice(0, maxConnections);

  let slavaLine: string;
  if (level === 'full') {
    slavaLine = connections.length
      ? 'Неделя собрана целиком — спасибо, это правда важно. Вот что я в ней вижу.'
      : 'Неделя собрана целиком. Явных закономерностей пока не набралось — и это нормально: они проявляются за месяц-два, а не за семь дней. Продолжай отмечать.';
  } else if (level === 'partial') {
    slavaLine = `Отметок ${filledDays} из 7 — на короткий разбор хватает. Полный будет, когда неделя соберётся целиком.`;
  } else {
    slavaLine = `Отметок пока ${filledDays}. Показываю, что уже видно, — но это только край картины.`;
  }

  return { filledDays, level, concern: false, facts, connections, slavaLine };
}
